import {
  type FC,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { type ActiveStorageBlob } from "react-activestorage";

import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { faArrowsRotate } from "@fortawesome/pro-duotone-svg-icons/faArrowsRotate";
import { faFolderOpen } from "@fortawesome/pro-duotone-svg-icons/faFolderOpen";
import { faXmark } from "@fortawesome/pro-duotone-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { type EvaluationTypeEnum } from "@app_schema";

import { AddRecordsUploadStep } from "./add_records_upload_forms";
import { ChoiceCard } from "./choice_card";
import { ClassifyOverlayShell } from "./classify_overlay_shell";

export type AddRecordsChoice = "additional_records" | "followup";

type ChoiceOption = {
  value: AddRecordsChoice;
  icon: IconProp;
  title: string;
  description: string;
};

const CHOICES: ReadonlyArray<ChoiceOption> = [
  {
    value: "additional_records",
    icon: faFolderOpen,
    title: "Additional Records",
    description: "Add more records to this existing review.",
  },
  {
    value: "followup",
    icon: faArrowsRotate,
    title: "Follow-Up Evaluation",
    description: "Compare against the existing review and flag what's new.",
  },
];

type UploadHandler = (
  kind: AddRecordsChoice,
  blob: ActiveStorageBlob,
) => Promise<void>;

/**
 * Two-step Add Records wizard rendered when the clinician clicks
 * `+ Add Records` on a complete-state RSB.
 *
 * Step 1 (B0.5 / B1) — `.classify-overlay` with two choice cards
 *   (Additional Records / Follow-Up). Picking a card transitions to step 2
 *   with no server round-trip.
 *
 * Step 2 (B0.75 Additional Records, B3 Follow-Up) — handled by
 *   `AddRecordsUploadStep`. For Additional Records the project's eval_type
 *   stays put; only Follow-Up fires the Comp→Follow-Up promotion mutation,
 *   and only on UPLOAD success — `← Change` returns to step 1 without
 *   flipping the project.
 *
 * Per design handoff D2.
 */
export const AddRecordsClassifyOverlay: FC<{
  contextPages: number;
  contextLetters: number;
  contextSummary?: string;
  evaluationType?: EvaluationTypeEnum | null;
  // When provided, step 1 (classify) is skipped and the overlay opens
  // straight to the upload form for this kind. Used when the project's own
  // evaluation type already determines the kind (Supplemental / Follow-Up
  // projects) — there's nothing to classify. When null, the user picks in
  // step 1 as usual.
  initialKind?: AddRecordsChoice | null;
  onUpload: UploadHandler;
  onDismiss: () => void;
}> = ({
  contextPages,
  contextLetters,
  contextSummary,
  evaluationType,
  initialKind,
  onUpload,
  onDismiss,
}) => {
  const [pickedKind, setPickedKind] = useState<AddRecordsChoice | null>(
    initialKind ?? null,
  );

  if (pickedKind === null) {
    return <ClassifyStep1 onPick={setPickedKind} onDismiss={onDismiss} />;
  }

  // When `initialKind` was provided the user landed directly on step 2 —
  // there's no step 1 to go back to, so hide `← Change` and expose a
  // dismiss (✕) affordance instead.
  const canChange = initialKind == null;

  return (
    <AddRecordsUploadStep
      kind={pickedKind}
      contextPages={contextPages}
      contextLetters={contextLetters}
      contextSummary={contextSummary}
      evaluationType={evaluationType}
      canChange={canChange}
      onUpload={(blob) => onUpload(pickedKind, blob)}
      onChange={() => setPickedKind(null)}
      onDismiss={onDismiss}
    />
  );
};

// ----- Step 1: classify choice cards (B0.5 / B1) -----------------------------

const ClassifyStep1: FC<{
  onPick: (choice: AddRecordsChoice) => void;
  onDismiss: () => void;
}> = ({ onPick, onDismiss }) => {
  const [selected, setSelected] =
    useState<AddRecordsChoice>("additional_records");
  // WAI-ARIA radiogroup pattern: arrow keys must move DOM focus alongside the
  // visual selection so Enter fires onClick on the correct card. One ref per
  // choice; order matches CHOICES so index-based focus lookup is trivial.
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    cardRefs.current[0]?.focus();
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const currentIndex = CHOICES.findIndex((c) => c.value === selected);
      const delta = event.key === "ArrowRight" ? 1 : -1;
      const nextIndex =
        (currentIndex + delta + CHOICES.length) % CHOICES.length;
      setSelected(CHOICES[nextIndex]!.value);
      cardRefs.current[nextIndex]?.focus();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      onDismiss();
    }
  };

  return (
    <ClassifyOverlayShell
      title="Classify these records"
      subtitle="What type of records are these?"
      trailing={
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss classification picker"
          className="cursor-pointer text-slate-400 hover:text-slate-600"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      }
    >
      <div
        role="radiogroup"
        aria-label="Upload classification"
        className="grid grid-cols-1 gap-[14px] md:grid-cols-2"
      >
        {CHOICES.map((choice, index) => (
          <ChoiceCard
            key={choice.value}
            innerRef={(el) => {
              cardRefs.current[index] = el;
            }}
            icon={choice.icon}
            title={choice.title}
            description={choice.description}
            selected={choice.value === selected}
            onClick={() => onPick(choice.value)}
            onKeyDown={handleKeyDown}
          />
        ))}
      </div>
    </ClassifyOverlayShell>
  );
};
